import {
  ChangeEvent, ReactElement, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'components/Checkbox';
import { CheckboxIcon, SearchNewIcon, XIcon } from 'icons';
import {
  filterMachines,
  getTotalPages,
  paginate,
  getRange,
  countSelected,
} from './helpers';
import { MachinesPagination } from './Pagination';
import {
  Container,
  Header,
  HeaderLeft,
  HeaderTitle,
  HeaderCounter,
  HeaderCounterBold,
  HeaderActions,
  ActionButton,
  SearchWrapper,
  SearchInputWrapper,
  SearchInputIcon,
  SearchInputBase,
  SearchInputClearBtn,
  ListWrapper,
  Row,
  RowLabel,
  EmptyStateWrapper,
  EmptyStateTitle,
  EmptyStateText,
  ClearSearchButton,
} from './styles';
import { MachineGroup } from './types';
import { colors } from '~/styles/colors';

interface LinkedMachinesBoxProps {
  groups: MachineGroup[];
  showDacAutSuffix?: boolean;
  onChange: () => void;
  itemsPerPage?: number;
}

const ROW_HEIGHT = 36;
const MAX_VISIBLE_ROWS = 10;
const LIST_PADDING = 6;
const LIST_MAX_HEIGHT = MAX_VISIBLE_ROWS * ROW_HEIGHT + LIST_PADDING;

export function LinkedMachinesBox({
  groups,
  showDacAutSuffix,
  onChange,
  itemsPerPage = 8,
}: Readonly<LinkedMachinesBoxProps>): ReactElement {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [committedQuery, setCommittedQuery] = useState('');
  const [page, setPage] = useState(1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const filtered = useMemo(
    () => filterMachines(groups, committedQuery),
    [groups, committedQuery],
  );

  const totalPages = getTotalPages(filtered.length, itemsPerPage);
  const safePage = Math.min(page, totalPages);
  const pageItems = paginate(filtered, safePage, itemsPerPage);
  const range = getRange(safePage, itemsPerPage, filtered.length);
  const selectedCount = countSelected(groups);
  const hasQuery = committedQuery.length > 0;

  const minHeight = itemsPerPage * ROW_HEIGHT + LIST_PADDING;

  function handleQueryChange(e: ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setCommittedQuery(value);
      setPage(1);
    }, 200);
  }

  function handleClearSearch() {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setQuery('');
    setCommittedQuery('');
    setPage(1);
  }

  function handleToggle(group: MachineGroup) {
    group.checked = !group.checked;
    onChange();
  }

  function handleSelectAll() {
    if (committedQuery) {
      filtered.forEach((g) => { g.checked = true; });
    } else {
      groups.forEach((g) => { g.checked = true; });
    }
    onChange();
  }

  function handleClear() {
    groups.forEach((g) => { g.checked = false; });
    onChange();
  }

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  return (
    <div>
      <Header>
        <HeaderLeft>
          <HeaderTitle>{t('maquinasVinculadas')}</HeaderTitle>
          <HeaderCounter>
            <HeaderCounterBold>{selectedCount}</HeaderCounterBold>
            {` ${t('deTotalSelecionadas', { total: groups.length })}`}
            {hasQuery ? (
              <span>{` · ${t(filtered.length === 1 ? 'nResultadoBusca' : 'nResultadosBusca', { n: filtered.length })}`}</span>
            ) : null}
          </HeaderCounter>
        </HeaderLeft>
        <HeaderActions>
          <ActionButton type="button" onClick={handleSelectAll}>
            {hasQuery
              ? t('selecionarVisiveis', { n: filtered.length })
              : t('selecionarTodas')}
          </ActionButton>
          <ActionButton type="button" dimmed onClick={handleClear} disabled={selectedCount === 0}>
            {t('limpar')}
          </ActionButton>
        </HeaderActions>
      </Header>

      <Container>
        <SearchWrapper>
          <SearchInputWrapper>
            <SearchInputIcon>
              <SearchNewIcon />
            </SearchInputIcon>
            <SearchInputBase
              type="text"
              value={query}
              placeholder={t('buscarMaquina')}
              onChange={handleQueryChange}
            />
            {query ? (
              <SearchInputClearBtn type="button" onClick={handleClearSearch} aria-label={t('limparBusca')}>
                <XIcon color={colors.icon['sub-500']} />
              </SearchInputClearBtn>
            ) : null}
          </SearchInputWrapper>
        </SearchWrapper>

        <ListWrapper height={minHeight} maxHeight={LIST_MAX_HEIGHT}>
          {pageItems.length === 0 ? (
            <EmptyStateWrapper>
              <EmptyStateTitle>{t('nenhumaMaquinaEncontrada')}</EmptyStateTitle>
              <EmptyStateText>
                {t('nadaCorrespondeBusca', { query: committedQuery })}
                {' '}
                <ClearSearchButton type="button" onClick={handleClearSearch}>
                  {t('limparBusca')}
                </ClearSearchButton>
              </EmptyStateText>
            </EmptyStateWrapper>
          ) : (
            pageItems.map((group) => (
              <Row
                key={group.value}
                onClick={() => handleToggle(group)}
              >
                <Checkbox size={18} checked={!!group.checked}>
                  {group.checked ? <CheckboxIcon /> : null}
                </Checkbox>
                <RowLabel>
                  {group.label}
                  {showDacAutSuffix && group.withDacAut && ` ${t('automatizadoPorDac')}`}
                </RowLabel>
              </Row>
            ))
          )}
        </ListWrapper>

        <MachinesPagination
          page={safePage}
          totalPages={totalPages}
          totalItems={filtered.length}
          rangeStart={range.start}
          rangeEnd={range.end}
          onPageChange={setPage}
        />
      </Container>
    </div>
  );
}
